'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

const HOVER_CLOSE_DELAY_MS = 140;

interface SingleOpenMenu {
  openMenu: string | null;
  /** Open `label` and close every other menu (null closes all). */
  setOpenMenu: (label: string | null) => void;
  /** Hover helpers: fine pointers open on enter and close shortly after leaving. */
  hoverOpen: (label: string | null) => void;
  hoverClose: (label: string) => void;
}

/**
 * Approved nav behaviour: only one dropdown open at a time; hover opens on fine
 * pointers (unless `hover: false`); Escape, outside click and focus leaving the nav close it.
 */
export function useSingleOpenMenu(
  navRef: RefObject<HTMLElement>,
  { hover = true }: { hover?: boolean } = {}
): SingleOpenMenu {
  const [openMenu, setOpenMenuState] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const setOpenMenu = useCallback((label: string | null) => {
    window.clearTimeout(timer.current);
    setOpenMenuState(label);
  }, []);

  const finePointer = useCallback(
    () => hover && window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    [hover]
  );

  const hoverOpen = useCallback(
    (label: string | null) => {
      if (finePointer()) setOpenMenu(label);
    },
    [finePointer, setOpenMenu]
  );

  const hoverClose = useCallback(
    (label: string) => {
      if (!finePointer()) return;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        const nav = navRef.current;
        if (
          nav &&
          nav.contains(document.activeElement) &&
          document.activeElement !== document.body
        ) {
          const active = (document.activeElement as HTMLElement).closest('details');
          if (active?.dataset.menu === label) return;
        }
        setOpenMenuState((current) => (current === label ? null : current));
      }, HOVER_CLOSE_DELAY_MS);
    },
    [finePointer, navRef]
  );

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const onDocumentClick = (event: MouseEvent) => {
      if (!nav.contains(event.target as Node)) setOpenMenu(null);
    };
    // Document-level: a hover-opened menu must also close on Escape while
    // focus is elsewhere. Focus only returns to the summary from inside the nav.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const open = nav.querySelector<HTMLDetailsElement>('details[open]');
      if (!open) return;
      const focusInNav = nav.contains(document.activeElement);
      setOpenMenu(null);
      if (focusInNav) open.querySelector('summary')?.focus();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (!nav.contains(event.relatedTarget as Node | null)) setOpenMenu(null);
    };
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    nav.addEventListener('focusout', onFocusOut);
    return () => {
      window.clearTimeout(timer.current);
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
      nav.removeEventListener('focusout', onFocusOut);
    };
  }, [navRef, setOpenMenu]);

  return { openMenu, setOpenMenu, hoverOpen, hoverClose };
}
